import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, Activity } from 'lucide-react';
import type { FaceRecognitionStatus } from '../../api/face-recognition.schemas';

interface StatusDistributionChartProps {
  data?: FaceRecognitionStatus;
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
}

/**
 * StatusDistributionChart Component
 * 
 * Displays a chart showing Face Recognition status distribution.
 * Shows Success, Pending, Error, and Failure operation breakdowns.
 * 
 * Requirements: 2.5, 6.2
 */
export function StatusDistributionChart({ 
  data, 
  loading = false, 
  error,
  onRetry
}: StatusDistributionChartProps) {
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Status Distribution</CardTitle>
          </div>
          <CardDescription>Operation status breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 space-y-4">
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="w-full h-3 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-4 w-24 mb-2" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="text-center space-y-1">
                  <Skeleton className="h-4 w-8 mx-auto" />
                  <Skeleton className="h-3 w-12 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Status Distribution</CardTitle>
          </div>
          <CardDescription>Failed to load status data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Status Distribution</CardTitle>
          </div>
          <CardDescription>Operation status breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Activity className="h-6 w-6 mx-auto mb-2" />
            <p>No status data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total and percentages
  const total = data.success + data.pending + data.error + data.failure;
  const statuses = [
    { 
      name: 'Success', 
      value: data.success, 
      color: 'bg-green-500', 
      textColor: 'text-green-700',
      description: 'Completed successfully'
    },
    { 
      name: 'Pending', 
      value: data.pending, 
      color: 'bg-blue-500', 
      textColor: 'text-blue-700',
      description: 'Awaiting completion'
    },
    { 
      name: 'Error', 
      value: data.error, 
      color: 'bg-red-500', 
      textColor: 'text-red-700',
      description: 'Technical error'
    },
    { 
      name: 'Failure', 
      value: data.failure, 
      color: 'bg-orange-500', 
      textColor: 'text-orange-700',
      description: 'Validation failed'
    },
  ].filter(item => item.value > 0); // Only show non-zero values

  if (total === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Status Distribution</CardTitle>
          </div>
          <CardDescription>Operation status breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Activity className="h-6 w-6 mx-auto mb-2" />
            <p>No status data recorded</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate success rate (success / completed operations)
  const completedOperations = data.success + data.error + data.failure;
  const successRate = completedOperations > 0 ? (data.success / completedOperations) * 100 : 0;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Status Distribution</CardTitle>
        </div>
        <CardDescription>
          {total.toLocaleString()} completed operations
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-4 overflow-y-auto">
          {statuses.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <span className="font-medium">{item.name}</span>
                    <span className="text-xs text-muted-foreground">({item.description})</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{item.value.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`${item.color} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Performance Summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Completed Operations Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="text-center">
              <div className="font-semibold text-green-600">{data.success}</div>
              <div className="text-muted-foreground">Success</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-blue-600">{data.pending}</div>
              <div className="text-muted-foreground">Pending</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-red-600">{data.error}</div>
              <div className="text-muted-foreground">Error</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-orange-600">{data.failure}</div>
              <div className="text-muted-foreground">Failure</div>
            </div>
          </div>
          {completedOperations > 0 && (
            <div className="mt-2 pt-2 border-t border-muted">
              <div className="text-center">
                <div className={`font-semibold ${successRate >= 95 ? 'text-green-600' : successRate >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {successRate.toFixed(1)}%
                </div>
                <div className="text-muted-foreground">Success Rate</div>
              </div>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-muted text-xs text-muted-foreground text-center">
            Only completed operations are shown
          </div>
        </div>
      </CardContent>
    </Card>
  );
}